import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adaptOutputContract } from "../adapters/outputContractAdapter";
import {
  checkBackendStatus,
  uploadFile,
  getIngestedData,
  prepareLCAPayload,
  createLcaRun,
  runLCA,
  getLcaRun,
  lockLcaRun,
  listLcaRuns,
  deleteLcaRun,
  downloadReport,
} from "../api";
import LCAResults from "../components/LCAResults";
import ReportPanel from "../components/ReportPanel";

const cardStyle = {
  marginBottom: 32,
  padding: 24,
  borderRadius: 16,
  background: "#ffffff",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  border: "1px solid #e6ecf5"
};

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://127.0.0.1:8000"
    : "");

function Dashboard() {
  const navigate = useNavigate();
  const [backendStatus, setBackendStatus] = useState("unknown");
  const [userPlan, setUserPlan] = useState("trial");

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMeta, setUploadMeta] = useState(null);
  const [lcaAssets, setLcaAssets] = useState([]);

  const [projectName, setProjectName] = useState("");
  const [runId, setRunId] = useState("");
  const [runIdInput, setRunIdInput] = useState("");
  const [projectIdInput, setProjectIdInput] = useState("");
  const [runState, setRunState] = useState("");

  const [projectRegion, setProjectRegion] = useState("Kenya");
  const [functionalUnitLabel, setFunctionalUnitLabel] = useState("m2");
  const [functionalUnitValue, setFunctionalUnitValue] = useState(100);

  const [outputContract, setOutputContract] = useState(null);

  const [ingestStatus, setIngestStatus] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [isIngesting, setIsIngesting] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uiMessage, setUiMessage] = useState({ type: "", text: "" });

  const getFriendlyIngestError = (err) => {
    const raw =
      String(err?.message || err?.error || err || "").toLowerCase();

    if (
      raw.includes("dataframe") &&
      raw.includes("dtype")
    ) {
      return (
        "Ingest failed. This Excel file structure is not supported.\n" +
        "Reason: one or more header columns are duplicated, merged, or malformed, so the system reads a full table where it expects a single column.\n" +
        "Fix the file by ensuring:\n" +
        "- Row 1 contains headers only\n" +
        "- No merged cells\n" +
        "- No duplicate column names\n" +
        "- No empty columns between data\n" +
        "- Data starts from row 2"
      );
    }

    if (raw.includes("forbidden")) {
      return "Ingest failed. You are not authorized to perform this action.";
    }

    if (raw.includes("upload not found")) {
      return "Ingest failed. The uploaded file reference was not found. Upload the file again, then retry ingest.";
    }

    if (raw.includes("missing")) {
      return "Ingest failed. Required file content is missing. Check that the BoQ has a proper header row and complete data.";
    }

    return (
      "Ingest failed. The uploaded Excel file does not match the accepted BoQ structure.\n" +
      "Accepted structure:\n" +
      "- Row 1 must contain headers only\n" +
      "- No merged cells\n" +
      "- No duplicate column names\n" +
      "- No empty columns between data\n" +
      "- Data must begin on row 2"
    );
  };

  useEffect(() => {
    checkBackendStatus()
      .then((res) => setBackendStatus(res?.status || "unknown"))
      .catch(() => setBackendStatus("unknown"));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/payments/subscription-status?user_id=1`)
      .then((res) => res.json())
      .then((data) => setUserPlan(data?.plan || "trial"))
      .catch(() => setUserPlan("trial"));
  }, []);

  const resolveProjectId = () =>
    projectIdInput?.trim() || projectName?.trim() || "";

  const resolveRunId = () => runIdInput?.trim() || runId?.trim() || "";

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      setUiMessage({ type: "", text: "" });

      const res = await uploadFile(selectedFile);
      setUploadMeta({
        upload_id: res.upload_id ?? res.id ?? null,
        filename: res.filename ?? selectedFile.name ?? null,
      });
      setUiMessage({
        type: "success",
        text: "File uploaded successfully. Proceed to ingest.",
      });
      setIngestStatus(null);
      setLcaAssets([]);
      setOutputContract(null);
    } catch (err) {
      console.error("Upload failed:", err);
      setUiMessage({ type: "error", text: "Upload failed. Check the console." });
    } finally {
      setIsUploading(false);
    }
  };

  const handleIngest = async () => {
    const uploadId = uploadMeta?.upload_id;
    if (!uploadId) return;

    try {
      setIsIngesting(true);
      setUiMessage({ type: "", text: "" });

      const res = await getIngestedData(uploadId);

      const assets =
        res?.lca_assets ||
        res?.assets ||
        res?.records ||
        res?.data?.assets ||
        [];

      setLcaAssets(Array.isArray(assets) ? assets : []);
      setIngestStatus({
        ok: true,
        assetCount: Array.isArray(assets) ? assets.length : 0,
      });
      setUiMessage({
        type: "success",
        text: `Ingest complete. Assets extracted: ${Array.isArray(assets) ? assets.length : 0}`,
      });

      const resolvedProjectId =
        res?.project_id ||
        res?.projectId ||
        res?.project?.id ||
        res?.project?.project_id ||
        res?.data?.project_id ||
        res?.data?.projectId ||
        res?.data?.project?.id ||
        res?.data?.project?.project_id ||
        "";

      if (resolvedProjectId) {
        setProjectName(String(resolvedProjectId));
        setProjectIdInput(String(resolvedProjectId));
      }

    } catch (err) {
      console.error("Ingest failed:", err);
      setIngestStatus({ ok: false });
      setLcaAssets([]);
      setUiMessage({ type: "error", text: getFriendlyIngestError(err) });
    } finally {
      setIsIngesting(false);
    }
  };

// -------------------------------
// Run LCA
// -------------------------------
  const handleRunLCA = async () => {
    if (!Array.isArray(lcaAssets) || lcaAssets.length === 0) {
      setUiMessage({
        type: "error",
        text: "No assets available. Ingest must succeed first.",
      });
      return;
    }

    if (!ingestStatus?.ok) {
      setUiMessage({
        type: "error",
        text: "Ingest must be completed before running LCA.",
      });
      return;
    }

    try {
      setIsRunning(true);
      setUiMessage({ type: "", text: "" });
      setOutputContract(null);
      setRunState("");
      setRunId("");
      setRunIdInput("");

      const projectId = resolveProjectId();

      if (!projectId) {
        setUiMessage({
          type: "error",
          text: "Project ID was not returned by backend ingest. Fix ingest response binding before running LCA.",
        });
        return;
      }
      const scenarioId = `scenario_${Date.now()}`;
      const scenarioName = "Baseline";

      let createData;

      try {
        createData = await createLcaRun({
          project_id: projectId,
          scenario_id: scenarioId,
          scenario_name: scenarioName,
        });
      } catch (err) {
        console.error("CREATE RUN ERROR:", err);
        setUiMessage({
          type: "error",
          text: err.message || "Draft run could not be created.",
        });
        return;
      }

      const activeRunId = createData?.run_id || "";

      const payload = {
        ...prepareLCAPayload(projectId, lcaAssets),
        project_id: projectId,
        scenario_id: scenarioId,
        scenario_name: scenarioName,
        project_region: projectRegion,
        functional_unit_label: functionalUnitLabel,
        functional_unit_value: functionalUnitValue,
        records: lcaAssets,
      };

      console.log("LCA PAYLOAD:", payload);

      const res = await runLCA(payload);

      console.log("LCA RESPONSE:", res);

      if (!res) {
        setUiMessage({ type: "error", text: "LCA execution failed. No response returned." });
        return;
      }

      if (res?.error_code === "RUN_TIMEOUT") {
        console.warn("LCA TIMEOUT — fallback to created run_id");

        const fallbackRunId = res?.run_id || activeRunId;

        if (fallbackRunId) {
          setRunId(fallbackRunId);
          setRunIdInput(fallbackRunId);
          setRunState("processing");

          setUiMessage({
            type: "warning",
            text: "Run started but timed out. You can fetch results using the run ID.",
          });
          return;
        }

        setUiMessage({ type: "error", text: "Run timed out and no run ID is available." });
        return;
      }

      if (res?.error_code || res?.error || res?.error_message) {
        console.error("LCA BACKEND ERROR:", res);
        setUiMessage({
          type: "error",
          text: res?.error_message || res?.error || "LCA execution failed.",
        });
        return;
      }

      if (!res?.run_id) {
        console.error("LCA RESPONSE MISSING run_id:", res);
        setUiMessage({ type: "error", text: "LCA execution failed. run ID missing." });
        return;
      }

      const resolvedRunId = res?.run_id || activeRunId;

      const rawOutputContract =
        res?.output_contract && typeof res.output_contract === "object"
          ? res.output_contract
          : {};

      const adaptedOutputContract = adaptOutputContract(rawOutputContract);
      const safeAdaptedOutputContract =
        adaptedOutputContract && typeof adaptedOutputContract === "object"
          ? adaptedOutputContract
          : {};

      const coverage = res?.coverage || {};

      const governedCount = coverage.governed_count ?? 0;
      const excludedCount = coverage.excluded_count ?? 0;

      const totalRecords =
        coverage.total_records ??
        (governedCount + excludedCount);

      const governedRatio =
        coverage.governed_ratio ??
        (totalRecords > 0 ? governedCount / totalRecords : 0);

      const finalOutputContract = {
        ...rawOutputContract,
        ...safeAdaptedOutputContract,
        metadata: rawOutputContract.metadata || {},

        coverage: {
          ...coverage,
          total_records: totalRecords,
          governed_count: governedCount,
          excluded_count: excludedCount,
          governed_ratio: governedRatio,
        },

        interpretation: {
          total_records: totalRecords,
          governed_count: governedCount,
          excluded_count: excludedCount,
          governed_ratio: governedRatio,
          top_exclusion_reason: res?.top_exclusion_reason || null,
          top_exclusion_material_keys: Array.isArray(res?.top_exclusion_material_keys)
            ? res.top_exclusion_material_keys
            : [],
          exclusion_reason_breakdown:
            coverage.exclusion_reason_breakdown || {},
        },

        top_exclusion_reason: res?.top_exclusion_reason || null,
        top_exclusion_count: res?.top_exclusion_count || 0,
        top_exclusion_material_keys: Array.isArray(res?.top_exclusion_material_keys)
          ? res.top_exclusion_material_keys
          : [],
      };

      setRunId(resolvedRunId);
      setRunIdInput(resolvedRunId);
      setRunState("completed");
      setOutputContract(finalOutputContract);
      setUiMessage({ type: "success", text: "LCA completed successfully." });
    } catch (err) {
      console.error("LCA raw error:", err);
      setUiMessage({ type: "error", text: "LCA failed. Check the console." });
    } finally {
      setIsRunning(false);
    }
  };

  const handleFetchRun = async () => {
    const res = await getLcaRun(resolveRunId());

    const rawOutputContract =
      res?.output_contract && typeof res.output_contract === "object"
        ? res.output_contract
        : {};

    const adaptedOutputContract = adaptOutputContract(rawOutputContract);
    const safeAdaptedOutputContract =
      adaptedOutputContract && typeof adaptedOutputContract === "object"
        ? adaptedOutputContract
        : {};

    setOutputContract({
      ...rawOutputContract,
      ...safeAdaptedOutputContract,
      metadata: rawOutputContract.metadata || {},
    });

    setRunState(res?.run_state || "");
  };

  const handleListRuns = async () => {
    await listLcaRuns(resolveProjectId());
  };

    const handleDeleteRun = async () => {
    await deleteLcaRun(resolveRunId(), resolveProjectId());
    setRunId("");
    setRunIdInput("");
    setRunState("");
    setOutputContract(null);
  };

  const handleLockRun = async () => {
    const rid = resolveRunId();

    if (!rid) {
      setUiMessage({ type: "error", text: "No run available to lock." });
      return;
    }
    if (runState !== "completed") {
      setUiMessage({
        type: "error",
        text: "Run must be completed before locking.",
      });
      return;
    }

    try {
      setIsLocking(true);
      setUiMessage({ type: "", text: "" });

      await lockLcaRun(rid);

    setRunState("locked");

    const refreshedRun = await getLcaRun(rid);

    const rawOutputContract =
      refreshedRun?.output_contract && typeof refreshedRun.output_contract === "object"
        ? refreshedRun.output_contract
        : {};

    const adaptedOutputContract = adaptOutputContract(rawOutputContract);
    const safeAdaptedOutputContract =
      adaptedOutputContract && typeof adaptedOutputContract === "object"
        ? adaptedOutputContract
        : {};

      setOutputContract((previous) => {
        const coverage =
          rawOutputContract.coverage ||
          previous?.coverage ||
          {};

        const governedCount = coverage.governed_count ?? 0;
        const excludedCount = coverage.excluded_count ?? 0;

        const totalRecords =
          coverage.total_records ??
          (governedCount + excludedCount);

        const governedRatio =
          coverage.governed_ratio ??
          (totalRecords > 0 ? governedCount / totalRecords : 0);

        return {
          ...rawOutputContract,
          ...safeAdaptedOutputContract,
          metadata: rawOutputContract.metadata || {},

          coverage: {
            ...coverage,
            total_records: totalRecords,
            governed_count: governedCount,
            excluded_count: excludedCount,
            governed_ratio: governedRatio,
          },

          interpretation: {
            total_records: totalRecords,
            governed_count: governedCount,
            excluded_count: excludedCount,
            governed_ratio: governedRatio,
            top_exclusion_reason:
              rawOutputContract.top_exclusion_reason ??
              previous?.top_exclusion_reason ??
              null,
            top_exclusion_material_keys: Array.isArray(
              rawOutputContract.top_exclusion_material_keys
            )
              ? rawOutputContract.top_exclusion_material_keys
              : Array.isArray(previous?.top_exclusion_material_keys)
              ? previous.top_exclusion_material_keys
              : [],
            exclusion_reason_breakdown:
              coverage.exclusion_reason_breakdown || {},
          },

          top_exclusion_reason:
            rawOutputContract.top_exclusion_reason ??
            previous?.top_exclusion_reason ??
            null,

          top_exclusion_count:
            rawOutputContract.top_exclusion_count ??
            previous?.top_exclusion_count ??
            0,

          top_exclusion_material_keys: Array.isArray(
            rawOutputContract.top_exclusion_material_keys
          )
            ? rawOutputContract.top_exclusion_material_keys
            : Array.isArray(previous?.top_exclusion_material_keys)
            ? previous.top_exclusion_material_keys
            : [],
        };
      });

    setRunState(refreshedRun?.run_state || "locked");

    setUiMessage({ type: "success", text: "Run locked successfully." });
    } catch (err) {
      console.error("Lock run failed:", err);
      setUiMessage({ type: "error", text: err.message || "Failed to lock run." });
    } finally {
      setIsLocking(false);
    }
  };

  const resolveStepLabel = () => {
    if (runState === "locked") return "REPORT";
    if (runState === "completed") return "REVIEW";
    if (ingestStatus?.ok) return "CONFIGURE";
    if (uploadMeta?.upload_id) return "INGEST";
    return "UPLOAD";
  };

  const step = resolveStepLabel();

  const isUpload = step === "UPLOAD";
  const isIngest = step === "INGEST";
  const isConfigure = step === "CONFIGURE";
  const isReview = step === "REVIEW";
  const isReport = step === "REPORT";

  const allowConfigure = isConfigure;
  const allowReview = isReview || isReport;
  const allowReport = runState === "locked";
  // PILOT OVERRIDE — allow PDF download for controlled pilot users
  const canDownloadPdf = true;

  const handleDownloadReport = async () => {
    const rid = resolveRunId();

    if (!rid) {
      setUiMessage({ type: "error", text: "No run ID available." });
      return;
    }

    try {
      setIsDownloading(true);
      setUiMessage({ type: "", text: "" });
      const blob = await downloadReport(rid);
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `BuildCarbonIQ_Report_${rid}_${Date.now()}.pdf`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setUiMessage({ type: "success", text: "PDF download started." });
    } catch (err) {
      console.error("Report download failed:", err);
      setUiMessage({ type: "error", text: err.message || "Report download failed." });
    } finally {
      setIsDownloading(false);
    }
  };
  
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: "260px 1fr",
        background: "#f4f8ff",
      }}
    >
      <aside
        style={{
          background: "#0d2b57",
          color: "#ffffff",
          padding: "24px",
        }}
      >
          <div
            style={{
              marginBottom: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <div style={{ position: "relative", display: "inline-flex" }}>
              <div
                style={{
                  position: "absolute",
                  inset: "-12px -16px",
                  background:
                    "radial-gradient(circle, rgba(56,189,248,0.18), rgba(15,23,42,0) 70%)",
                  filter: "blur(8px)",
                }}
              />
              <img
                src="/buildcarboniq-logo.png"
                alt="BuildCarbonIQ"
                style={{
                  width: 220,
                  height: "auto",
                  position: "relative",
                  zIndex: 1,
                  filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.25))",
                }}
              />
            </div>
          </div>

          <div style={{ display: "grid", gap: 14, marginTop: 20 }}>
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(255,255,255,0.18)",
              fontWeight: 700,
            }}
          >
            Dashboard
          </div>
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "transparent",
              fontWeight: 500,
              opacity: 0.65,
            }}
          >
            Projects
          </div>
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "transparent",
              fontWeight: 500,
              opacity: 0.65,
            }}
          >
            Reports
          </div>
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "transparent",
              fontWeight: 500,
              opacity: 0.65,
            }}
          >
            Datasets
          </div>
        </div>

        <div
          style={{
            marginTop: 32,
            paddingTop: 20,
            borderTop: "1px solid rgba(255,255,255,0.15)",
            fontSize: 12,
            lineHeight: 1.8,
            opacity: 0.95,
          }}
        >
          <div style={{ marginBottom: 10, fontWeight: 700 }}>Welcome message!</div>
          <div>Supported formats: .XLSX, .CSV</div>
          <div>Dataset: ÖKOBAUDAT Open 2024_10</div>
          <div>Dataset region: Europe (DE)</div>
        </div>
      </aside>

      <div style={{ padding: "32px 40px" }}>
        <div
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <div
            style={{
              marginBottom: 32,
              padding: 28,
              borderRadius: 18,
              background: "#ffffff",
              boxShadow: "0 10px 28px rgba(0,0,0,0.06)",
              border: "1px solid #e6ecf5",
            }}
          >
            <h1 style={{ margin: 0, fontSize: "30px", fontWeight: 700 }}>
              Main Dashboard
            </h1>

            <p style={{ margin: "6px 0 14px 0", color: "#5f6f86", fontSize: "15px" }}>
              Four-step guided workflow to complete your assessment.
            </p>

            <div
              style={{
                display: "inline-block",
                padding: "6px 14px",
                borderRadius: "999px",
                background: "#e8f0ff",
                color: "#1e40af",
                fontWeight: 600,
                fontSize: "13px",
              }}
            >
              Current stage: {resolveStepLabel()}
            </div>
          </div>

          {uiMessage.text && (
            <div
              style={{
                marginBottom: 24,
                padding: "16px 22px",
                borderRadius: 12,
                fontWeight: 600,
                border: "1px solid",
                background:
                  uiMessage.type === "success"
                    ? "#dcfce7"
                    : uiMessage.type === "warning"
                    ? "#fff7ed"
                    : "#fef2f2",
                borderColor:
                  uiMessage.type === "success"
                    ? "#86efac"
                    : uiMessage.type === "warning"
                    ? "#fdba74"
                    : "#fca5a5",
                color:
                  uiMessage.type === "success"
                    ? "#166534"
                    : uiMessage.type === "warning"
                    ? "#9a3412"
                    : "#991b1b",
              }}
            >
              {uiMessage.text}
            </div>
          )}

          <section
            style={{
              ...cardStyle,
              display: "block",
              opacity: isUpload || isIngest ? 1 : 0.55,
              borderLeft: isUpload || isIngest ? "4px solid #1e40af" : "4px solid transparent",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "#1e40af",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  marginRight: 12,
                }}
              >
                1
              </div>
              <h3 style={{ margin: 0 }}>Upload & Ingest</h3>
            </div>

            <input type="file" accept=".xls,.xlsx" onChange={(e) => setSelectedFile(e.target.files[0])} />

            <div style={{ marginTop: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <button
                onClick={handleUpload}
                disabled={isUploading}
                style={{
                  background: "linear-gradient(135deg, #1e40af, #2563eb)",
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(30,64,175,0.3)",
                  color: "#ffffff",
                  border: "none",
                  padding: "12px 18px",
                  borderRadius: 8,
                  cursor: "pointer",
                  opacity: isUploading ? 0.6 : 1,
                }}
              >
                {isUploading ? "Uploading..." : "Upload file"}
              </button>

              <button
                onClick={handleIngest}
                disabled={isIngesting || !uploadMeta?.upload_id}
                style={{
                  background: "linear-gradient(135deg, #1e40af, #2563eb)",
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(30,64,175,0.3)",
                  color: "#ffffff",
                  border: "none",
                  padding: "12px 18px",
                  borderRadius: 8,
                  cursor: "pointer",
                  opacity: isIngesting || !uploadMeta?.upload_id ? 0.6 : 1,
                }}
              >
                {isIngesting ? "Ingesting..." : "Ingest data"}
              </button>
            </div>

            <p style={{ marginBottom: 12, color: "#555" }}>
              Upload an Excel file to begin a new LCA analysis.
            </p>

            <div
              style={{
                marginBottom: 14,
                padding: "12px 14px",
                borderRadius: 8,
                background: "#fff7ed",
                border: "1px solid #fdba74",
                color: "#9a3412",
                fontSize: "13px",
                lineHeight: 1.7,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Accepted Excel structure
              </div>
              <div>• File type: .xls or .xlsx</div>
              <div>• Row 1 must contain column headers only</div>
              <div>• No merged header cells</div>
              <div>• No duplicate column names</div>
              <div>• No empty columns between data</div>
              <div>• Data must start from row 2</div>
            </div>

            {uploadMeta && (
              <p style={{ marginBottom: 8 }}>
                Selected file: {uploadMeta.filename}
              </p>
            )}
          </section>

          <section
            style={{
              ...cardStyle,
              display: "block",
              opacity: allowConfigure ? 1 : 0.55,
              borderLeft: isConfigure ? "4px solid #1e40af" : "4px solid transparent",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "#1e40af",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  marginRight: 12,
                }}
              >
                2
              </div>
              <h3 style={{ margin: 0 }}>Project Configuration</h3>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                Project ID
              </label>
              <input
                value={projectName}
                readOnly
                placeholder="Project ID will be assigned automatically after ingest"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #d9e2f1",
                  borderRadius: 8,
                  background: "#f8fbff",
                  boxSizing: "border-box",
                  color: projectName ? "#14223b" : "#64748b",
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                Project Region
              </label>
              <input
                value={projectRegion}
                onChange={(e) => setProjectRegion(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #d9e2f1",
                  borderRadius: 8,
                  background: "#f8fbff",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                Functional Unit Label
              </label>
              <input
                value={functionalUnitLabel}
                onChange={(e) => setFunctionalUnitLabel(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #d9e2f1",
                  borderRadius: 8,
                  background: "#f8fbff",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                Functional Unit Value
              </label>
              <input
                type="number"
                value={functionalUnitValue}
                onChange={(e) => setFunctionalUnitValue(Number(e.target.value))}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #d9e2f1",
                  borderRadius: 8,
                  background: "#f8fbff",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {!ingestStatus?.ok && (
              <p style={{ fontWeight: "bold" }}>
                Upload and ingest a file to begin.
              </p>
            )}

            {ingestStatus?.ok && runState !== "locked" && (
              <p style={{ fontWeight: "bold" }}>
                Configure project inputs and run the assessment.
              </p>
            )}

            <button
              onClick={handleRunLCA}
              disabled={
                isRunning ||
                runState === "completed" ||
                runState === "locked" ||
                !ingestStatus?.ok ||
                !Array.isArray(lcaAssets) ||
                lcaAssets.length === 0
              }
              style={{
                background: "linear-gradient(135deg, #1e40af, #2563eb)",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(30,64,175,0.3)",
                color: "#ffffff",
                border: "none",
                padding: "12px 18px",
                borderRadius: 8,
                cursor: "pointer",
                opacity:
                  isRunning ||
                  !ingestStatus?.ok ||
                  !Array.isArray(lcaAssets) ||
                  lcaAssets.length === 0
                    ? 0.6
                    : 1,
              }}
            >
              {isRunning ? "Running..." : "Run LCA assessment"}
            </button>
          </section>

          <section
            style={{
              ...cardStyle,
              display: "block",
              opacity: allowReview ? 1 : 0.55,
              borderLeft: isReview ? "4px solid #1e40af" : "4px solid transparent",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "#1e40af",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  marginRight: 12,
                }}
              >
                3
              </div>
              <h3 style={{ margin: 0 }}>Review & Report</h3>
            </div>

            {(runState === "completed" || runState === "locked") && (
              <button
                onClick={handleLockRun}
                disabled={isLocking || !outputContract || !resolveRunId()}
                style={{
                  background: "linear-gradient(135deg, #1e40af, #2563eb)",
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(30,64,175,0.3)",
                  border: "none",
                  color: "#ffffff",
                  padding: "12px 18px",
                  borderRadius: 8,
                  cursor: "pointer",
                  opacity:
                    isLocking || !outputContract || !resolveRunId()
                      ? 0.6
                      : 1,
                }}
              >
                {isLocking ? "Locking..." : "Lock run"}
              </button>
            )}

            <div style={{ marginTop: 16 }}>
              <ReportPanel
                runId={resolveRunId()}
                runState={runState}
                onDownloadPDF={handleDownloadReport}
                outputContract={outputContract}
                isDownloading={isDownloading}
                canDownloadPdf={canDownloadPdf}
                userPlan={userPlan}
              />
            </div>
          </section>

          <section
            style={{
              ...cardStyle,
              display: "block",
              opacity: runState === "locked" ? 1 : 0.55,
              borderLeft: isReport ? "4px solid #16a34a" : "4px solid transparent",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 24,
                paddingBottom: 12,
                borderBottom: "1px solid #e6ecf5",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "#1e40af",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  marginRight: 12,
                }}
              >
                4
              </div>
              <h3 style={{ margin: 0, fontSize: "22px", fontWeight: 800, color: "#14223b" }}>
                Results Overview
              </h3>
            </div>

            {!outputContract && (
              <p style={{ color: "#555" }}>
                Results will appear here after completing and locking the run.
              </p>
            )}

            {runState === "locked" && outputContract && (
              <div
                style={{
                  marginTop: 12,
                  padding: 6,
                  borderRadius: 14,
                  background: "#f8fbff",
                  border: "1px solid #e6ecf5",
                }}
              >
                <div style={{ padding: "10px 10px 0 10px" }}>
                  <button
                    onClick={() => {
                      if (runState !== "locked") {
                        setUiMessage({
                          type: "error",
                          text: "Run must be completed before viewing results.",
                        });
                        return;
                      }

                      navigate("/results", {
                        state: { outputContract },
                      });
                    }}
                    style={{
                      background: "linear-gradient(135deg, #1e40af, #2563eb)",
                      color: "#ffffff",
                      border: "none",
                      padding: "12px 18px",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontWeight: 600,
                      marginBottom: 16,
                    }}
                  >
                    Open Detailed Results Interpretation
                  </button>
                </div>

                <LCAResults outputContract={outputContract} />
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
